import React, { useCallback } from 'react';

import type { ControllerRenderProps } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';

import { z } from 'zod';

import Input from 'components/Input/Input';
import { cn } from 'components/lib/utils';
import RadioButtonGroup from 'components/RadioButton/RadioButtonGroup';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from 'components/ui/form';

import {
  ContentSelectionMode,
  ERROR_FIELD_CLASS,
  IBlockContentProps
} from '../../../../constants';
import {
  contentsPreviewBlockSchema,
  pageFormSchema
} from '../../../../validation-schema';

import { ContentItemsSelect } from './ContentItemsSelect';

type ContentsPreviewBlockType = z.infer<typeof contentsPreviewBlockSchema>;
type PageFormType = z.infer<typeof pageFormSchema>;

type ContentsPreviewFieldProps<T extends keyof ContentsPreviewBlockType> =
  ControllerRenderProps<PageFormType, any> & {
    value: ContentsPreviewBlockType[T];
  };

const CONTENT_SELECTION_OPTIONS = [
  { value: ContentSelectionMode.LAST_CONTENTS, id: 'lastContents', label: 'Max Items' },
  {
    value: ContentSelectionMode.USER_SELECTED_CONTENTS,
    id: 'userSelectedContents',
    label: 'Select Specific Items'
  }
];

interface ContentSelectionSectionProps
  extends Pick<IBlockContentProps, 'blockIndex'> {
  subType: string;
  contentItemsOptions: { value: string; label: string }[];
  getFieldError: (fieldName: string) => { message?: string } | undefined;
}

export const ContentSelectionSection: React.FC<ContentSelectionSectionProps> = ({
  blockIndex,
  subType,
  contentItemsOptions,
  getFieldError
}) => {
  const { control, setValue, clearErrors, trigger } =
    useFormContext<z.infer<typeof pageFormSchema>>();
  const basePath = `blocksV2.${blockIndex}` as const;

  const handleSelectionModeChange = useCallback(
    (newValue: string) => {
      setValue(
        `${basePath}.subType`,
        newValue as ContentSelectionMode,
        { shouldValidate: false }
      );
      if (newValue === ContentSelectionMode.LAST_CONTENTS) {
        setValue(`${basePath}.maxItems`, 1000, { shouldValidate: false });
        setValue(`${basePath}.selectedContentsIds`, [], { shouldValidate: false });
        trigger(`${basePath}.maxItems` as any);
        trigger(`${basePath}.selectedContentsIds` as any);
      } else {
        setValue(`${basePath}.maxItems`, undefined, { shouldValidate: false });
        setValue(`${basePath}.selectedContentsIds`, [], { shouldValidate: false });
        clearErrors(`${basePath}.maxItems` as any);
        clearErrors(`${basePath}.selectedContentsIds` as any);
      }
    },
    [basePath, setValue, clearErrors, trigger]
  );

  const renderContentValue = useCallback(
    (ids: string[]) => {
      if (!ids.length) return '';
      if (ids.length === 1) {
        const item = contentItemsOptions.find((opt) => opt.value === ids[0]);
        return item?.label ?? ids[0];
      }
      if (ids.length === contentItemsOptions.length) return 'All';
      return ids
        .map(
          (id) =>
            contentItemsOptions.find((opt) => opt.value === id)?.label ?? id
        )
        .join(', ');
    },
    [contentItemsOptions]
  );

  return (
    <>
      <FormField
        control={control}
        name={`${basePath}.subType`}
        render={({
          field: { value }
        }: {
          field: ContentsPreviewFieldProps<'subType'>;
        }) => (
          <FormItem>
            <FormLabel>* Content Selection</FormLabel>
            <FormControl>
              <RadioButtonGroup
                value={value ?? ContentSelectionMode.LAST_CONTENTS}
                onValueChange={handleSelectionModeChange}
                options={CONTENT_SELECTION_OPTIONS}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {subType === ContentSelectionMode.LAST_CONTENTS && (
        <FormField
          control={control}
          name={`${basePath}.maxItems`}
          render={({
            field
          }: {
            field: ContentsPreviewFieldProps<'maxItems'>;
          }) => (
            <FormItem>
              <FormLabel>* Max Items</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  placeholder="Enter max items"
                  min={1}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const val = e.target.value;
                    field.onChange(val === '' ? null : Number(val));
                  }}
                  onBlur={() => {
                    field.onBlur();
                    trigger('blocksV2');
                  }}
                  value={field.value != null ? field.value : ''}
                  className={cn(
                    getFieldError(`${basePath}.maxItems`)?.message
                      ? ERROR_FIELD_CLASS
                      : ''
                  )}
                  state={
                    getFieldError(`${basePath}.maxItems`)?.message
                      ? 'error'
                      : 'default'
                  }
                />
              </FormControl>
              <FormMessage className="mt-1 text-p-s" />
            </FormItem>
          )}
        />
      )}
      {subType === ContentSelectionMode.USER_SELECTED_CONTENTS && (
        <FormField
          control={control}
          name={`${basePath}.selectedContentsIds`}
          render={({
            field: { onChange, value }
          }: {
            field: ContentsPreviewFieldProps<'selectedContentsIds'>;
          }) => (
            <FormItem>
              <FormLabel>* Select Content Items</FormLabel>
              <FormControl>
                <ContentItemsSelect
                  options={contentItemsOptions}
                  value={value ?? []}
                  onChange={onChange}
                  onClose={() => trigger('blocksV2')}
                  placeholder="Search and select items"
                  renderValue={renderContentValue}
                  hasError={!!getFieldError(`${basePath}.selectedContentsIds`)?.message}
                />
              </FormControl>
              <FormMessage className="mt-1 text-p-s" />
            </FormItem>
          )}
        />
      )}
    </>
  );
};
